import { useState, useEffect, useCallback } from 'react';

export type Theme = 'dark' | 'ash' | 'twilight' | 'void' | 'eclipse' | 'dawn';
export type FontSize = 'small' | 'medium' | 'large';

interface ThemeState {
  theme: Theme;
  fontSize: FontSize;
  highContrast: boolean;
}

const STORAGE_KEY = 'theme-preferences';

const getDefaultState = (): ThemeState => ({
  theme: 'dark',
  fontSize: 'medium',
  highContrast: false,
});

const loadState = (): ThemeState => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migrate old themes to dark
      if (['light', 'system', 'bone', 'fog'].includes(parsed.theme)) {
        parsed.theme = 'dark';
      }
      return parsed;
    }
    return getDefaultState();
  } catch {
    return getDefaultState();
  }
};

const applyTheme = (theme: Theme, highContrast: boolean) => {
  const root = document.documentElement;
  
  root.classList.remove('dark', 'bone', 'ash', 'fog', 'twilight', 'void', 'eclipse', 'dawn', 'high-contrast');
  root.classList.add(theme);
  if (highContrast) root.classList.add('high-contrast');
};

const applyFontSize = (size: FontSize) => {
  const root = document.documentElement;
  root.classList.remove('text-sm', 'text-base', 'text-lg');
  root.style.fontSize = size === 'small' ? '14px' : size === 'large' ? '18px' : '16px';
};

export const useTheme = () => {
  const [state, setState] = useState<ThemeState>(loadState);

  // Apply theme on mount and changes
  useEffect(() => {
    applyTheme(state.theme, state.highContrast);
    applyFontSize(state.fontSize);
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state]);

  const setTheme = useCallback((theme: Theme) => {
    setState(prev => ({ ...prev, theme }));
  }, []);

  const setFontSize = useCallback((fontSize: FontSize) => {
    setState(prev => ({ ...prev, fontSize }));
  }, []);

  const setHighContrast = useCallback((highContrast: boolean) => {
    setState(prev => ({ ...prev, highContrast }));
  }, []);

  return {
    theme: state.theme,
    fontSize: state.fontSize,
    highContrast: state.highContrast,
    setTheme,
    setFontSize,
    setHighContrast,
  };
};
